/**
 * Sales > Price & Distribution — top items across retailers: a heatmap of ACV
 * distribution or base price, and a price ladder (each item's lowest, median and
 * highest base price across the retailers shown).
 */

import { useMemo, useState } from 'react'
import type { EChartsOption } from 'echarts'

import Chart from '../../components/Chart'
import { DataTable } from '../../components/DataTable'
import { formatCurrency } from '../../lib/format'
import type { CustomReportProps } from '../index'
import {
  chart,
  colorsFor,
  num,
  records,
  type Row,
} from './shared'
import { Empty, Panel, Tile } from './ui'

type Metric = 'acv' | 'base_price'

function shortItem(name: string): string {
  // Provider descriptions end with the UPC ("... 10 OZ 54400-00005"); keep the words.
  return name.replace(/\s+\d{5}-\d{5}$/, '').slice(0, 48)
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const s = [...values].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? (s[mid] ?? null) : ((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2
}

export function PriceDistribution({ result, department }: CustomReportProps) {
  const [metric, setMetric] = useState<Metric>('acv')
  const { accent, second: light } = colorsFor(department)
  const rows = records(result)

  const shape = useMemo(() => {
    const items = new Map<string, number>()
    const geos = new Map<string, number>()
    for (const r of rows) {
      items.set(String(r.item), num(r.item_rank) ?? 999)
      geos.set(String(r.geography), num(r.geo_rank) ?? 999)
    }
    const itemList = [...items.entries()].sort((a, b) => a[1] - b[1]).map(([k]) => k)
    const geoList = [...geos.entries()].sort((a, b) => a[1] - b[1]).map(([k]) => k)
    return { itemList, geoList }
  }, [rows])

  const heatOption = useMemo<EChartsOption>(() => {
    const { itemList, geoList } = shape
    const values = rows
      .map((r) => num(r[metric]))
      .filter((v): v is number => v !== null)
    const data = rows.map((r: Row) => [
      geoList.indexOf(String(r.geography)),
      itemList.indexOf(String(r.item)),
      num(r[metric]),
    ])
    return {
      ...chart.baseOption(),
      grid: { left: 8, right: 8, top: 8, bottom: 70, containLabel: true },
      legend: { show: false },
      tooltip: {
        trigger: 'item',
        formatter: (p: unknown) => {
          const [g, i, v] = (p as { data: [number, number, number | null] }).data
          const value =
            v === null ? '—' : metric === 'acv' ? `${String(v)}% ACV` : formatCurrency(v)
          return `${shape.itemList[i] ?? ''}<br>${shape.geoList[g] ?? ''}: <b>${value}</b>`
        },
      },
      xAxis: {
        type: 'category',
        data: geoList,
        axisLabel: { ...chart.axisLabel(), rotate: 40, fontSize: 10, fontFamily: 'IBM Plex Sans, sans-serif' },
        splitArea: { show: true },
      },
      yAxis: {
        type: 'category',
        data: itemList.map(shortItem),
        inverse: true,
        axisLabel: { ...chart.axisLabel(), fontSize: 10, fontFamily: 'IBM Plex Sans, sans-serif' },
        splitArea: { show: true },
      },
      visualMap: {
        min: values.length ? Math.min(...values) : 0,
        max: values.length ? Math.max(...values) : 1,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        itemHeight: 160,
        inRange: { color: metric === 'acv' ? ['#f3f4f6', light, accent] : ['#e8f3ec', '#f6e7a8', '#e08a6b'] },
        formatter: (v: unknown) =>
          metric === 'acv' ? `${String(Math.round(Number(v)))}%` : formatCurrency(Number(v)),
        textStyle: { fontSize: 10 },
      },
      series: [
        {
          type: 'heatmap',
          data,
          label: {
            show: itemList.length * geoList.length <= 300,
            fontSize: 9,
            formatter: (p: unknown) => {
              const v = (p as { data: [number, number, number | null] }).data[2]
              if (v === null) return ''
              return metric === 'acv' ? String(v) : v.toFixed(2)
            },
          },
          emphasis: { itemStyle: { borderColor: '#333', borderWidth: 1 } },
        },
      ],
    }
  }, [rows, shape, metric, accent, light])

  const ladder = useMemo(() => {
    return shape.itemList.map((item) => {
      const prices = rows
        .filter((r) => String(r.item) === item)
        .map((r) => num(r.base_price))
        .filter((v): v is number => v !== null && v > 0)
      return {
        item,
        min: prices.length ? Math.min(...prices) : null,
        max: prices.length ? Math.max(...prices) : null,
        med: median(prices),
      }
    })
  }, [rows, shape])

  const ladderOption = useMemo<EChartsOption>(() => {
    const items = [...ladder].reverse()
    return {
      ...chart.baseOption(),
      grid: { left: 8, right: 16, top: 8, bottom: 28, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (p: unknown) => {
          const idx = (p as { dataIndex: number }[])[0]?.dataIndex ?? 0
          const l = items[idx]
          if (l === undefined) return ''
          return `${shortItem(l.item)}<br>Low ${formatCurrency(l.min)} · Median ${formatCurrency(l.med)} · High ${formatCurrency(l.max)}`
        },
      },
      legend: { show: false },
      xAxis: {
        type: 'value',
        scale: true,
        axisLabel: { ...chart.axisLabel(), formatter: (v: number) => `$${v.toFixed(2)}` },
        splitLine: chart.splitLine(),
      },
      yAxis: {
        type: 'category',
        data: items.map((l) => shortItem(l.item)),
        axisLabel: { ...chart.axisLabel(), fontSize: 10, fontFamily: 'IBM Plex Sans, sans-serif' },
        axisTick: { show: false },
        axisLine: chart.axisLine(),
      },
      series: [
        {
          type: 'bar',
          stack: 'range',
          itemStyle: { color: 'transparent' },
          data: items.map((l) => l.min),
          barMaxWidth: 12,
          silent: true,
        },
        {
          type: 'bar',
          stack: 'range',
          itemStyle: { color: light, borderRadius: 6 },
          data: items.map((l) => (l.min !== null && l.max !== null ? l.max - l.min : null)),
          barMaxWidth: 12,
        },
        {
          type: 'scatter',
          symbolSize: 10,
          itemStyle: { color: accent },
          data: items.map((l) => l.med),
        },
      ],
    }
  }, [ladder, accent, light])

  if (rows.length === 0) return <Empty>No items for these filters.</Empty>

  const acvValues = rows.map((r) => num(r.acv)).filter((v): v is number => v !== null)
  const spreads = ladder
    .filter((l) => l.min !== null && l.max !== null && l.min > 0)
    .map((l) => (l.max ?? 0) / (l.min || 1) - 1)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="Items x retailers" value={`${String(shape.itemList.length)} x ${String(shape.geoList.length)}`} />
        <Tile
          label="Average ACV"
          value={
            acvValues.length
              ? `${String(Math.round(acvValues.reduce((a, b) => a + b, 0) / acvValues.length))}%`
              : '—'
          }
        />
        <Tile
          label="Typical price spread"
          value={spreads.length ? `${String(Math.round((median(spreads) ?? 0) * 100))}%` : '—'}
          note="high vs low retailer"
          delta=" "
        />
      </div>
      <div className="grid grid-cols-12 gap-4">
        <Panel wide title={metric === 'acv' ? 'ACV distribution by retailer' : 'Base price by retailer'}>
          <div className="mb-2 flex gap-1" role="group" aria-label="Heatmap measure">
            {(['acv', 'base_price'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMetric(m)
                }}
                className={`rounded-[var(--radius-md)] border px-2.5 py-1 text-[length:var(--text-xs)] ${
                  metric === m
                    ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                    : 'border-[var(--color-rule)]'
                }`}
              >
                {m === 'acv' ? 'ACV %' : 'Base price'}
              </button>
            ))}
          </div>
          <Chart
            option={heatOption}
            height={Math.max(320, 26 * shape.itemList.length + 120)}
            ariaLabel="Item by retailer heatmap"
          />
        </Panel>
        <Panel
          wide
          title="Price ladder"
          subtitle="Each item's lowest to highest base price across the retailers shown; dot = median"
        >
          <Chart
            option={ladderOption}
            height={Math.max(280, 24 * ladder.length + 60)}
            ariaLabel="Base price range by item"
          />
        </Panel>
      </div>
      <DataTable columns={result.columns} rows={result.rows} caption="Item by retailer" />
    </div>
  )
}
